import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { GetCountries, StateSelect } from 'react-country-state-city';
import { DateRange } from 'react-date-range';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import Hls from 'hls.js';
import {
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  Copy,
  PlayCircle,
  Search,
  Sparkles,
  X
} from 'lucide-react';
import 'react-country-state-city/dist/react-country-state-city.css';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { ianaTimezones } from '../../../constants';
import { post_api as postApi } from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';
import * as actions from '../../../redux/action_creators';
import './teacher_match.css';

// Options must stay in lockstep with the goserver personality vocabulary
// (personalityDimensions in app/services/teachermatch/personality.go). A teacher
// is classified into these exact strings, so any drift here silently stops
// classified teachers from matching the filter chips.
const dimensionFilters = [
  {
    key: 'teaching_style',
    label: 'Instruction Style',
    options: ['Structured', 'Simple', 'Precise']
  },
  {
    key: 'class_energy',
    label: 'Intensity',
    options: ['Restorative', 'Gentle', 'Moderate', 'Vigorous']
  },
  {
    key: 'personality',
    label: 'Interaction Style',
    options: ['Friendly', 'Professional', 'Playful']
  },
  {
    key: 'philosophy',
    label: 'Primary Focus',
    options: [
      'Pain Relief & Therapy',
      'Breath & Meditation',
      'Lifestyle & Wellness',
      'Strength & Fitness'
    ]
  }
];

const operationalFilters = [
  {
    key: 'country',
    label: 'Country',
    options: ['India', 'United States', 'Canada', 'United Kingdom']
  },
  {
    key: 'state',
    label: 'State',
    options: [
      'Karnataka',
      'Maharashtra',
      'Tamil Nadu',
      'Delhi',
      'California',
      'Texas',
      'New York',
      'Ontario',
      'British Columbia',
      'England'
    ]
  },
  {
    key: 'language',
    label: 'Language spoken',
    options: [
      'Afrikaans',
      'Arabic',
      'Armenian',
      'Assamese',
      'Azerbaijani',
      'Belarusian',
      'Bengali',
      'Bodo',
      'Bosnian',
      'Bulgarian',
      'Catalan',
      'Cebuano',
      'Chichewa',
      'Croatian',
      'Czech',
      'Danish',
      'Dogri',
      'Dutch',
      'English',
      'Estonian',
      'Filipino',
      'Finnish',
      'French',
      'Galician',
      'Georgian',
      'German',
      'Greek',
      'Gujarati',
      'Hausa',
      'Hebrew',
      'Hindi',
      'Hungarian',
      'Icelandic',
      'Indonesian',
      'Irish',
      'Italian',
      'Japanese',
      'Javanese',
      'Kannada',
      'Kashmiri',
      'Kazakh',
      'Kirghiz',
      'Konkani',
      'Korean',
      'Latvian',
      'Lingala',
      'Lithuanian',
      'Luxembourgish',
      'Macedonian',
      'Maithili',
      'Malay',
      'Malayalam',
      'Mandarin Chinese',
      'Manipuri',
      'Marathi',
      'Nepali',
      'Norwegian',
      'Odia',
      'Pashto',
      'Persian',
      'Polish',
      'Portuguese',
      'Punjabi',
      'Romanian',
      'Russian',
      'Sanskrit',
      'Santali',
      'Serbian',
      'Sindhi',
      'Slovak',
      'Slovenian',
      'Somali',
      'Spanish',
      'Swahili',
      'Swedish',
      'Tamil',
      'Telugu',
      'Thai',
      'Turkish',
      'Ukrainian',
      'Urdu',
      'Vietnamese',
      'Welsh'
    ]
  },
  {
    // Checkbox: checked sends ethnicity_match=WESTERN (the backend contract is
    // unchanged); unchecked clears the filter (All).
    key: 'ethnicity_match',
    label: 'Comfortable with western',
    options: ['WESTERN']
  },
  { key: 'conversion', label: 'Conversion', options: ['Great', 'Medium', 'Poor'] },
  {
    key: 'goal',
    label: 'Goal Supported',
    options: [
      'Build Strength',
      'Increase Flexibility',
      'Boost Energy Levels',
      'Reduce Aches and Pain',
      'Stress Relief',
      'Lose Weight',
      'Manage a Health Condition',
      'Sports Injury',
      'Prenatal or Postnatal'
    ]
  },
  {
    key: 'specialties',
    label: 'Specialties',
    options: [
      'Aerial',
      'Cardio',
      'Work with weights',
      'Work with club',
      'Sleep',
      'Kids',
      'Chair',
      'Iyengar',
      'Prenatal',
      'Postnatal',
      'Ashtanga',
      'Gentle',
      'Hatha',
      'Kundalini',
      'Meditation',
      'Power',
      'Pranayama',
      'Restorative',
      'Vinyasa',
      'Yin',
      'Fusion'
    ]
  },
  { key: 'gender', label: 'Gender', options: ['Female', 'Male'] },
  {
    key: 'age',
    label: 'Age',
    options: ['20-25', '25-30', '30-35', '35-40', '40-50', '50-60', '60+']
  },
  {
    key: 'experience',
    label: 'Years Experience',
    options: ['0-5', '5-10', '10-20', '20+']
  },
  {
    key: 'platform_years',
    label: 'Years on Platform',
    options: ['< 3 months', '3-6 months', '6 months - year', '1-3 years', '3+ years']
  },
  {
    key: 'base_rate',
    label: 'Teacher Base Rate',
    options: ['less than 500', '500-599', '600-699', '700-799', '799+']
  },
  { key: 'availability', label: 'Availability', options: ['Morning', 'Afternoon', 'Evening'] },
  {
    key: 'repeat_available',
    label: 'Repeat Availability',
    options: ['Same slot free next week']
  },
  { key: 'hide_from_onboarding', label: 'Hide From Onboarding', options: ['Hidden'] },
  { key: 'hide_from_new_students', label: 'Hide From New Students', options: ['Hidden'] },
  { key: 'hide_from_replacement', label: 'Hide From Replacement', options: ['Hidden'] },
  { key: 'allow_instant_booking', label: 'Allow Instant Booking', options: ['Enabled'] }
];

// Checkbox filters rendered inside the Availability section, in array order
// (Repeat Availability first, then the teacher-visibility / booking flags).
const sessionFilterKeys = [
  'repeat_available',
  'hide_from_onboarding',
  'hide_from_new_students',
  'hide_from_replacement',
  'allow_instant_booking'
];
const sessionFilters = operationalFilters.filter((filter) =>
  sessionFilterKeys.includes(filter.key)
);

const sessionDurationFilter = {
  key: 'session_duration',
  label: 'Session Duration (Mins)',
  options: ['30', '45', '60', '90']
};

const recurringDays = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' }
];

const EXPERIENCE_MIN = 0;
const EXPERIENCE_MAX = 20;

function formatExperienceValue(years) {
  return years >= EXPERIENCE_MAX ? `${EXPERIENCE_MAX}+` : `${years}`;
}

function isExperienceDefault([min, max]) {
  return min <= EXPERIENCE_MIN && max >= EXPERIENCE_MAX;
}

const BASE_RATE_MIN = 0;
const BASE_RATE_MAX = 800;

function formatBaseRateValue(rate) {
  return rate >= BASE_RATE_MAX ? '799+' : `${rate}`;
}

function isBaseRateDefault([min, max]) {
  return min <= BASE_RATE_MIN && max >= BASE_RATE_MAX;
}

const AGE_MIN = 20;
const AGE_MAX = 60;

function formatAgeValue(age) {
  return age >= AGE_MAX ? `${AGE_MAX}+` : `${age}`;
}

function isAgeDefault([min, max]) {
  return min <= AGE_MIN && max >= AGE_MAX;
}

// Years on Platform is a stepped (ordinal) slider — each step is one of the
// dropdown options, in ascending order. The slider value is a [minIndex, maxIndex].
const platformYearsSteps = operationalFilters.find(
  (filter) => filter.key === 'platform_years'
).options;
const PLATFORM_YEARS_MIN = 0;
const PLATFORM_YEARS_MAX = platformYearsSteps.length - 1;

function formatPlatformYears(index) {
  return platformYearsSteps[index];
}

function isPlatformYearsDefault([min, max]) {
  return min <= PLATFORM_YEARS_MIN && max >= PLATFORM_YEARS_MAX;
}

const availabilityWindows = [
  { key: 'early_morning', label: 'Early Morning (5:00 AM – 7:00 AM)' },
  { key: 'morning', label: 'Morning (7:00 AM – 11:00 AM)' },
  { key: 'afternoon', label: 'Afternoon (11:00 AM – 4:00 PM)' },
  { key: 'early_evening', label: 'Early Evening (4:00 PM – 7:00 PM)' },
  { key: 'evening', label: 'Evening (7:00 PM – 10:00 PM)' },
  { key: 'late_night', label: 'Late Night (10:00 PM onwards)' }
];

const exactTimeOptions = Array.from({ length: 37 }, (_, index) => {
  const totalMinutes = 4 * 60 + index * 30;
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return {
    value: `${`${hour}`.padStart(2, '0')}:${`${minute}`.padStart(2, '0')}`,
    label: `${`${displayHour}`.padStart(2, '0')}:${`${minute}`.padStart(2, '0')} ${period}`
  };
});

const timezoneOptions = ianaTimezones.flatMap((group) =>
  group.options.map((option) => ({
    ...option,
    group: group.label
  }))
);

// The country filter is limited to the countries represented in ianaTimezones,
// identified by their iso2 codes.
const TIMEZONE_COUNTRY_ISO2 = new Set(
  ianaTimezones.flatMap((group) => group.options.map((option) => option.iso2)).filter(Boolean)
);

// Common informal names/abbreviations that don't already match a supported
// country's ISO code, official name, or timezone label (e.g. "UK" -> GB).
// Keyed by iso2, values are lowercase aliases. Only includes countries present
// in ianaTimezones above.
const COUNTRY_SEARCH_ALIASES = {
  US: ['america'], // "usa"/"us" already match the group label and iso2
  GB: ['uk', 'britain', 'great britain', 'england', 'scotland', 'wales'],
  AE: ['uae'], // label is "United Arab Emirates"
  SA: ['ksa'], // Kingdom of Saudi Arabia
  ZA: ['rsa'], // Republic of South Africa
  NL: ['holland'],
  AU: ['aus', 'oz'],
  NZ: ['kiwi', 'aotearoa'],
  IE: ['eire'],
  GR: ['hellas'],
  CY: ['kibris'],
  SG: ['sgp']
};

// Conversion is a minimum-quality slider: picking a level includes that level
// and every better one (e.g. Medium -> Medium + Great). "Any" = no filter.
const CONVERSION_LEVELS = ['Any', 'Poor', 'Medium', 'Great'];
const CONVERSION_BUCKETS_ASC = ['POOR', 'MEDIUM', 'GREAT']; // worst -> best
const CONVERSION_MIN = 0;
const CONVERSION_MAX = CONVERSION_LEVELS.length - 1;

// The Teacher Country filter defaults to India.
const DEFAULT_TEACHER_COUNTRY = 'India';

function getInitialFilters() {
  return {
    ...dimensionFilters.reduce((acc, filter) => ({ ...acc, [filter.key]: [] }), {}),
    ...operationalFilters.reduce(
      (acc, filter) => ({
        ...acc,
        [filter.key]: ['goal', 'language', 'specialties'].includes(filter.key) ? [] : 'All'
      }),
      {}
    ),
    country: DEFAULT_TEACHER_COUNTRY,
    experience: [EXPERIENCE_MIN, EXPERIENCE_MAX],
    base_rate: [BASE_RATE_MIN, BASE_RATE_MAX],
    platform_years: [PLATFORM_YEARS_MIN, PLATFORM_YEARS_MAX],
    age: [AGE_MIN, AGE_MAX],
    conversion: CONVERSION_MIN, // 0 = Any (minimum-quality slider)
    // Availability filters (timezone / session date / duration / windows) are
    // stubbed on the backend for now, so they default to neutral values.
    timezone: 'All',
    availability_windows: [],
    availability_exact_times: [],
    session_duration: 'All',
    session_date: null,
    recurring_days: []
  };
}

// Reference timezone for availability day/time filters when none is selected.
// Kept in sync with the backend default (teachermatch availability.go).
const DEFAULT_AVAILABILITY_TZ = 'America/Los_Angeles';

// Local date → 'YYYY-MM-DD' (no UTC shift).
function formatYmd(date) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// buildSearchPayload maps the UI filter state to the POST /v2/teachermatch/search
// request body. Only the backend-implemented filters are sent; base rate is
// intentionally omitted (no backend column yet).
function buildSearchPayload(filters) {
  const payload = {};

  if (filters.country && filters.country !== 'All') payload.countries = [filters.country];
  if (filters.state && filters.state !== 'All') payload.states = [filters.state];

  // Personality Dimensions: send each dimension with a non-empty selection.
  const dimensions = {};
  dimensionFilters.forEach((filter) => {
    const selected = filters[filter.key];
    if (Array.isArray(selected) && selected.length > 0) {
      dimensions[filter.key] = selected;
    }
  });
  if (Object.keys(dimensions).length > 0) payload.dimensions = dimensions;

  if (Array.isArray(filters.language) && filters.language.length > 0) {
    payload.languages = filters.language;
  }
  if (Array.isArray(filters.goal) && filters.goal.length > 0) payload.goals = filters.goal;
  if (Array.isArray(filters.specialties) && filters.specialties.length > 0) {
    payload.specialties = filters.specialties;
  }

  if (filters.gender && filters.gender !== 'All') payload.genders = [filters.gender.toUpperCase()];

  if (filters.ethnicity_match && filters.ethnicity_match !== 'All') {
    payload.ethnicity_match = filters.ethnicity_match;
  }

  // Conversion: minimum level -> that bucket and every better one.
  const conversionIdx =
    typeof filters.conversion === 'number' ? filters.conversion : CONVERSION_MIN;
  if (conversionIdx > CONVERSION_MIN) {
    payload.conversions = CONVERSION_BUCKETS_ASC.slice(conversionIdx - 1);
  }
  // Teacher visibility flags (checked => require the flag set on the teacher).
  if (filters.hide_from_onboarding && filters.hide_from_onboarding !== 'All') {
    payload.hide_from_onboarding = true;
  }
  if (filters.hide_from_new_students && filters.hide_from_new_students !== 'All') {
    payload.hide_from_new_students = true;
  }
  if (filters.hide_from_replacement && filters.hide_from_replacement !== 'All') {
    payload.hide_from_replacement = true;
  }
  if (filters.allow_instant_booking && filters.allow_instant_booking !== 'All') {
    payload.allow_instant_booking = true;
  }

  // Repeat availability: teacher must have a matching slot that is also free
  // exactly 7 days later (bookable as a weekly-repeating session).
  if (filters.repeat_available && filters.repeat_available !== 'All') {
    payload.repeat_available = true;
  }

  const experienceRange = filters.experience || [EXPERIENCE_MIN, EXPERIENCE_MAX];
  if (!isExperienceDefault(experienceRange)) {
    const [expMin, expMax] = experienceRange;
    const experience = {};
    if (expMin > EXPERIENCE_MIN) experience.min = expMin;
    if (expMax < EXPERIENCE_MAX) experience.max = expMax;
    payload.experience = experience;
  }

  const ageRange = filters.age || [AGE_MIN, AGE_MAX];
  if (!isAgeDefault(ageRange)) {
    const [ageMin, ageMax] = ageRange;
    const age = {};
    if (ageMin > AGE_MIN) age.min = ageMin;
    if (ageMax < AGE_MAX) age.max = ageMax;
    payload.age = age;
  }

  const baseRateRange = filters.base_rate || [BASE_RATE_MIN, BASE_RATE_MAX];
  if (!isBaseRateDefault(baseRateRange)) {
    const [brMin, brMax] = baseRateRange;
    const baseRate = {};
    if (brMin > BASE_RATE_MIN) baseRate.min = brMin;
    if (brMax < BASE_RATE_MAX) baseRate.max = brMax;
    payload.base_rate = baseRate;
  }

  const platformYearsRange = filters.platform_years || [PLATFORM_YEARS_MIN, PLATFORM_YEARS_MAX];
  if (!isPlatformYearsDefault(platformYearsRange)) {
    const [pyMin, pyMax] = platformYearsRange;
    const platformYears = {};
    if (pyMin > PLATFORM_YEARS_MIN) platformYears.min = pyMin;
    if (pyMax < PLATFORM_YEARS_MAX) platformYears.max = pyMax;
    payload.platform_years = platformYears;
  }

  // --- Availability ---
  if (filters.session_duration && filters.session_duration !== 'All') {
    payload.session_duration = filters.session_duration;
  }
  if (Array.isArray(filters.availability_windows) && filters.availability_windows.length > 0) {
    payload.availability_windows = filters.availability_windows;
  }
  if (
    Array.isArray(filters.availability_exact_times) &&
    filters.availability_exact_times.length > 0
  ) {
    payload.availability_times = filters.availability_exact_times;
  }
  if (Array.isArray(filters.recurring_days) && filters.recurring_days.length > 0) {
    payload.recurring_days = filters.recurring_days;
  }
  if (filters.session_date && filters.session_date.startDate) {
    payload.session_date = {
      start: formatYmd(filters.session_date.startDate),
      end: formatYmd(filters.session_date.endDate || filters.session_date.startDate)
    };
  }

  // Reference timezone for the day/time/date availability filters. Send the
  // selected one, or the explicit default when "All" while availability is active.
  const availabilityActive =
    !!payload.session_duration ||
    !!payload.availability_windows ||
    !!payload.availability_times ||
    !!payload.recurring_days ||
    !!payload.session_date;
  if (filters.timezone && filters.timezone !== 'All') {
    payload.timezone = filters.timezone;
  } else if (availabilityActive) {
    payload.timezone = DEFAULT_AVAILABILITY_TZ;
  }

  return payload;
}

// VideoPlayer plays the teacher intro video. Vimeo links are HLS (.m3u8) which
// Chrome's <video> can't play natively, so we use hls.js (Safari plays HLS
// natively). AWS links are direct .mp4 and play as a normal source.
function VideoPlayer({ url }) {
  const ref = useRef(null);

  useEffect(() => {
    const video = ref.current;
    if (!video || !url) return undefined;

    const isHls = /\.m3u8(\?|$)/i.test(url);

    if (!isHls) {
      // Direct file (mp4) or a browser with native HLS support (Safari).
      video.src = url;
      return undefined;
    }

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(video);
      return () => hls.destroy();
    }

    // Last resort — let the browser try the URL directly.
    video.src = url;
    return undefined;
  }, [url]);

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video ref={ref} className="teacher-match-video-player" controls autoPlay />
  );
}

VideoPlayer.propTypes = {
  url: PropTypes.string.isRequired
};

// mapCardToTeacher adapts an API TeacherCard to the shape the result list and
// search box expect. Fields not yet provided by the backend (match score, next
// slot, base rate, personality profile) fall back to neutral placeholders.
function mapCardToTeacher(card, index) {
  return {
    uuid: card.uuid,
    slug: card.slug || '',
    // The teacher's "mini story" (teacher_metadata.intro_to_students), used in the share message.
    story: card.intro_to_students || '',
    // Rank is pinned to the API response order; client-side search must not renumber it.
    rank: index + 1,
    name: card.name || '',
    profile_photo: card.profile_photo || '',
    video_url: card.video_url || '',
    location: card.location || '',
    city: card.city || '',
    country: card.country || '',
    state: card.state || '',
    certificates: Array.isArray(card.certificates) ? card.certificates : [],
    timezone: card.timezone || '',
    language: card.language || '',
    gender: card.gender || '',
    experience: card.experience || '',
    age: card.age_bucket || '',
    platform_years: card.platform_years || '',
    conversion: card.conversion || '—',
    conversion_score: typeof card.conversion_score === 'number' ? card.conversion_score : null,
    trial: card.trial ? 'Offers trial sessions' : '',
    goals: Array.isArray(card.goals) ? card.goals : [],
    base_rate:
      typeof card.base_rate === 'number' ? `₹${card.base_rate.toLocaleString('en-IN')}` : '—',
    match_score: typeof card.match_percent === 'number' ? card.match_percent : null,
    match_factors: Array.isArray(card.match_factors) ? card.match_factors : [],
    profile: card.profile || {}
  };
}

function getInitials(name) {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function profileTags(profile) {
  return dimensionFilters
    .map((filter) => ({
      key: filter.key,
      label: filter.label.replace(' Style', ''),
      value: profile[filter.key]
    }))
    .filter((tag) => tag.value);
}

// Public-facing teacher profile URL, e.g. https://myyogateacher.com/yoga-teachers/aarthi-2
const PUBLIC_PROFILE_BASE = 'https://myyogateacher.com/yoga-teachers/';

function publicProfileUrl(teacher) {
  return teacher.slug ? `${PUBLIC_PROFILE_BASE}${teacher.slug}` : '';
}

// buildExpertiseBlurb writes a one-line pitch tied to the ACTIVE filters: it
// leads with the personality dimensions the user filtered on (falling back to a
// couple of sensible defaults), then the goals matching the goal filter, plus
// experience and languages. This keeps the shared blurb relevant to what the
// person searching actually cares about.
function buildExpertiseBlurb(teacher, filters) {
  const profile = teacher.profile || {};

  const selectedDims = dimensionFilters
    .map((f) => f.key)
    .filter((key) => Array.isArray(filters[key]) && filters[key].length > 0);
  const descriptorKeys = selectedDims.length ? selectedDims : ['class_energy', 'philosophy'];
  const descriptors = descriptorKeys.map((key) => profile[key]).filter(Boolean);

  const goalFilter = Array.isArray(filters.goal) ? filters.goal : [];
  const matchedGoals = goalFilter.length
    ? teacher.goals.filter((g) => goalFilter.includes(g))
    : teacher.goals;
  const goals = (matchedGoals.length ? matchedGoals : teacher.goals).slice(0, 3);

  let sentence = descriptors.length ? `${descriptors.join(', ')} yoga teacher` : 'Yoga teacher';
  if (teacher.experience) sentence += ` with ${teacher.experience} yrs of experience`;
  if (goals.length) sentence += `, specializing in ${goals.join(', ')}`;
  sentence += '.';
  if (teacher.language) sentence += ` Speaks ${teacher.language}.`;
  return sentence;
}

// trimStory normalizes whitespace and caps the mini-story to keep the shared
// message compact (WhatsApp/email friendly).
function trimStory(story) {
  const s = (story || '').replace(/\s+/g, ' ').trim();
  if (s.length <= 320) return s;
  return `${s.slice(0, 317).trimEnd()}…`;
}

// buildShareMessage composes the shareable blurb for one teacher: name,
// filter-aware expertise line, mini story, and the public profile URL.
function buildShareMessage(teacher, filters) {
  const lines = [teacher.name, buildExpertiseBlurb(teacher, filters)];
  const story = trimStory(teacher.story);
  if (story) lines.push(story);
  const url = publicProfileUrl(teacher);
  if (url) lines.push(url);
  return lines.filter(Boolean).join('\n');
}

function buildShareMessageForMany(list, filters) {
  return list.map((t) => buildShareMessage(t, filters)).join('\n\n———\n\n');
}

// copyToClipboard uses the async Clipboard API with a legacy execCommand
// fallback (older browsers / non-secure contexts). Resolves to a boolean.
async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // fall through to the legacy path
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (err) {
    return false;
  }
}

function LabeledPill({ label, value, tone = 'neutral', icon = null }) {
  return (
    <span className={`teacher-match-labeled-pill teacher-match-labeled-pill-${tone}`}>
      {icon ? <span className="teacher-match-pill-icon">{icon}</span> : null}
      <span className="teacher-match-pill-label">{label}</span>
      <span className="teacher-match-pill-value">{value}</span>
    </span>
  );
}

LabeledPill.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  // eslint-disable-next-line react/require-default-props
  tone: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  icon: PropTypes.node
};

function getFilterTriggerLabel(value) {
  if (!Array.isArray(value)) return value;
  if (value.length === 0) return 'All';
  if (value.length <= 2) return value.join(', ');
  return `${value.length} selected`;
}

// Decide whether an options menu should open upward, based on the room left
// below the trigger within the viewport. Re-measures whenever the menu opens.
function useDropDirection(ref, isOpen, menuHeight = 240) {
  const [dropUp, setDropUp] = useState(false);

  useEffect(() => {
    if (!isOpen || !ref.current) return;
    const triggerRect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    setDropUp(spaceBelow < menuHeight && spaceAbove > spaceBelow);
  }, [ref, isOpen, menuHeight]);

  return dropUp;
}

function getTimezoneLabel(value) {
  if (value === 'All') return 'All';
  const timezone = timezoneOptions.find((option) => option.value === value);
  return timezone ? `${timezone.label} - ${timezone.value}` : value;
}

function getAvailabilityWindowLabel(value) {
  return availabilityWindows.find((window) => window.key === value)?.label || value;
}

function getExactTimeLabel(value) {
  return exactTimeOptions.find((time) => time.value === value)?.label || value;
}

function formatDateLabel(date) {
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

// Session date selection window: today through today + 14 days (15 days inclusive,
// e.g. Jun 24 → Jul 8).
const SESSION_DATE_MAX_DAYS = 14;

function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

// session_date is either null or { startDate, endDate }. A single day is start === end.
function formatDateRangeLabel(range) {
  if (!range || !range.startDate) return '';
  const start = formatDateLabel(new Date(range.startDate));
  const end = range.endDate ? formatDateLabel(new Date(range.endDate)) : start;
  return start === end ? start : `${start} - ${end}`;
}

// Quick-filter presets for the session date picker. Each returns a [start, end]
// pair relative to `today`; both ends are clamped to the selectable window.
// Week boundaries follow the calendar (Sunday start, Saturday end).
const SESSION_DATE_PRESETS = [
  { key: 'all', label: 'All', range: null },
  { key: 'today', label: 'Today', range: (t) => [t, t] },
  { key: 'tomorrow', label: 'Tomorrow', range: (t) => [addDays(t, 1), addDays(t, 1)] },
  { key: 'next_2_days', label: 'Next 2 days', range: (t) => [t, addDays(t, 1)] },
  { key: 'next_7_days', label: 'Next 7 days', range: (t) => [t, addDays(t, 6)] },
  { key: 'this_week', label: 'This week', range: (t) => [t, addDays(t, 6 - t.getDay())] },
  {
    key: 'next_week',
    label: 'Next week',
    range: (t) => {
      const nextSunday = addDays(t, 7 - t.getDay());
      return [nextSunday, addDays(nextSunday, 6)];
    }
  },
  { key: 'this_fortnight', label: 'This fortnight', range: (t) => [t, addDays(t, 13)] }
];

function getActiveFilterChips(filters, searchValue) {
  const searchChip = searchValue.trim()
    ? [{ key: 'search', label: 'Search', value: searchValue.trim(), filterKey: 'search' }]
    : [];

  const dimensionChips = dimensionFilters.flatMap((filter) =>
    filters[filter.key].map((value) => ({
      key: `${filter.key}-${value}`,
      label: filter.label.replace(' Style', ''),
      value,
      filterKey: filter.key
    }))
  );

  const operationalChips = operationalFilters.flatMap((filter) => {
    if (['experience', 'base_rate', 'platform_years', 'age', 'conversion'].includes(filter.key))
      return [];
    const value = filters[filter.key];
    if (Array.isArray(value)) {
      return value.map((selectedValue) => ({
        key: `${filter.key}-${selectedValue}`,
        label: filter.label,
        value: selectedValue,
        filterKey: filter.key
      }));
    }

    return value !== 'All'
      ? [
          {
            key: filter.key,
            label: filter.label,
            value,
            filterKey: filter.key
          }
        ]
      : [];
  });

  const conversionIdx =
    typeof filters.conversion === 'number' ? filters.conversion : CONVERSION_MIN;
  const conversionChip =
    conversionIdx > CONVERSION_MIN
      ? [
          {
            key: 'conversion',
            label: 'Conversion',
            value: `${CONVERSION_LEVELS[conversionIdx]}${conversionIdx < CONVERSION_MAX ? ' & above' : ''}`,
            filterKey: 'conversion'
          }
        ]
      : [];

  const timezoneChip =
    filters.timezone !== 'All'
      ? [
          {
            key: 'timezone',
            label: 'Timezone',
            value: getTimezoneLabel(filters.timezone),
            filterKey: 'timezone'
          }
        ]
      : [];

  const sessionDurationChip =
    filters.session_duration && filters.session_duration !== 'All'
      ? [
          {
            key: 'session-duration',
            label: 'Duration',
            value: `${filters.session_duration} mins`,
            filterKey: 'session_duration'
          }
        ]
      : [];

  const sessionDateChip = filters.session_date
    ? [
        {
          key: 'session-date',
          label: 'Session date',
          value: formatDateRangeLabel(filters.session_date),
          filterKey: 'session_date'
        }
      ]
    : [];

  const experienceRange = filters.experience || [EXPERIENCE_MIN, EXPERIENCE_MAX];
  const experienceChip = isExperienceDefault(experienceRange)
    ? []
    : [
        {
          key: 'experience',
          label: 'Experience',
          value: `${formatExperienceValue(experienceRange[0])} - ${formatExperienceValue(experienceRange[1])} yrs`,
          filterKey: 'experience'
        }
      ];

  const baseRateRange = filters.base_rate || [BASE_RATE_MIN, BASE_RATE_MAX];
  const baseRateChip = isBaseRateDefault(baseRateRange)
    ? []
    : [
        {
          key: 'base_rate',
          label: 'Base rate',
          value: `${formatBaseRateValue(baseRateRange[0])} - ${formatBaseRateValue(baseRateRange[1])}`,
          filterKey: 'base_rate'
        }
      ];

  const platformYearsRange = filters.platform_years || [PLATFORM_YEARS_MIN, PLATFORM_YEARS_MAX];
  const platformYearsChip = isPlatformYearsDefault(platformYearsRange)
    ? []
    : [
        {
          key: 'platform_years',
          label: 'Years on platform',
          value: `${formatPlatformYears(platformYearsRange[0])} - ${formatPlatformYears(platformYearsRange[1])}`,
          filterKey: 'platform_years'
        }
      ];

  const ageRange = filters.age || [AGE_MIN, AGE_MAX];
  const ageChip = isAgeDefault(ageRange)
    ? []
    : [
        {
          key: 'age',
          label: 'Age',
          value: `${formatAgeValue(ageRange[0])} - ${formatAgeValue(ageRange[1])}`,
          filterKey: 'age'
        }
      ];

  const recurringDayChips = (filters.recurring_days || []).map((dayKey) => ({
    key: `recurring-day-${dayKey}`,
    label: 'Day',
    value: recurringDays.find((day) => day.key === dayKey)?.label || dayKey,
    rawValue: dayKey,
    filterKey: 'recurring_days'
  }));

  const availabilityWindowChips = (filters.availability_windows || []).map((value) => ({
    key: `availability-window-${value}`,
    label: 'Availability',
    value: getAvailabilityWindowLabel(value),
    rawValue: value,
    filterKey: 'availability_windows'
  }));

  const availabilityExactTimeChips = (filters.availability_exact_times || []).map((value) => ({
    key: `availability-exact-${value}`,
    label: 'Exact time',
    value: getExactTimeLabel(value),
    rawValue: value,
    filterKey: 'availability_exact_times'
  }));

  return [
    ...searchChip,
    ...dimensionChips,
    ...operationalChips,
    ...conversionChip,
    ...experienceChip,
    ...baseRateChip,
    ...platformYearsChip,
    ...ageChip,
    ...availabilityWindowChips,
    ...availabilityExactTimeChips,
    ...timezoneChip,
    ...sessionDurationChip,
    ...sessionDateChip,
    ...recurringDayChips
  ];
}

function resetDocumentScroll() {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

function FilterDropdown({ filter, value, isOpen, onToggle, onChange, searchable }) {
  const ref = useRef(null);
  const [query, setQuery] = useState('');
  const isMultiSelect = Array.isArray(value);
  const selectedValues = isMultiSelect ? value : [];
  const hasSelection = isMultiSelect ? selectedValues.length > 0 : value !== 'All';
  const triggerLabel = getFilterTriggerLabel(value);
  const dropUp = useDropDirection(ref, isOpen);

  const normalizedQuery = query.trim().toLowerCase();
  const options = normalizedQuery
    ? filter.options.filter((option) => option.toLowerCase().includes(normalizedQuery))
    : ['All', ...filter.options];

  const isOptionSelected = (option) => {
    if (isMultiSelect)
      return option === 'All' ? selectedValues.length === 0 : selectedValues.includes(option);
    return value === option;
  };

  const selectOption = (option) => {
    if (!isMultiSelect) {
      onChange(filter.key, option);
      onToggle('');
      return;
    }

    if (option === 'All') {
      onChange(filter.key, []);
      return;
    }

    onChange(
      filter.key,
      selectedValues.includes(option)
        ? selectedValues.filter((selectedValue) => selectedValue !== option)
        : [...selectedValues, option]
    );
  };

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      return undefined;
    }
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onToggle('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  return (
    <div className={`teacher-match-field ${isOpen ? 'teacher-match-field-open' : ''}`} ref={ref}>
      <div className="teacher-match-field-label">{filter.label}</div>
      <button
        type="button"
        className={`teacher-match-select-trigger ${hasSelection ? 'teacher-match-selected' : ''}`}
        onClick={() => onToggle(isOpen ? '' : filter.key)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{triggerLabel}</span>
        <ChevronDown size={15} className={isOpen ? 'teacher-match-chevron-open' : ''} />
      </button>

      {isOpen ? (
        <div
          className={`teacher-match-options ${dropUp ? 'teacher-match-options-up' : ''}`}
          role="listbox"
          aria-multiselectable={isMultiSelect}
        >
          {searchable ? (
            <div className="teacher-match-timezone-search">
              <Search size={14} />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={`Search ${filter.label.toLowerCase()}`}
              />
            </div>
          ) : null}
          {options.map((option) => (
            <button
              key={option}
              type="button"
              className="teacher-match-option"
              onClick={() => selectOption(option)}
              role="option"
              aria-selected={isOptionSelected(option)}
            >
              <span>{option}</span>
              {isOptionSelected(option) ? <Check size={14} /> : null}
            </button>
          ))}
          {searchable && options.length === 0 ? (
            <div className="teacher-match-option-empty">No matches</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function FilterCheckbox({ filter, value, onChange }) {
  const isChecked = value === filter.options[0];
  const inputId = `teacher-match-${filter.key}`;

  return (
    <label
      className={`teacher-match-checkbox ${isChecked ? 'teacher-match-checkbox-selected' : ''}`}
      htmlFor={inputId}
    >
      <input
        id={inputId}
        type="checkbox"
        checked={isChecked}
        onChange={(event) => onChange(filter.key, event.target.checked ? filter.options[0] : 'All')}
      />
      <span className="teacher-match-checkbox-control">
        {isChecked ? <Check size={14} /> : null}
      </span>
      <span className="teacher-match-checkbox-text">{filter.label}</span>
    </label>
  );
}

function FilterSegmentedControl({ filter, value, onChange, className }) {
  // Options may be plain strings (value === label) or { value, label } pairs
  // when the display text differs from the value sent to the backend.
  const options = [
    { value: 'All', label: 'All' },
    ...filter.options.map((option) =>
      typeof option === 'string' ? { value: option, label: option } : option
    )
  ];

  return (
    <div className={`teacher-match-field ${className}`}>
      <div className="teacher-match-field-label">{filter.label}</div>
      <div
        className="teacher-match-segmented"
        role="group"
        aria-label={filter.label}
        style={{ '--segment-count': options.length }}
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`teacher-match-segment ${value === option.value ? 'teacher-match-segment-selected' : ''}`}
            onClick={() => onChange(filter.key, option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function RecurringDaysSelector({ value, onChange, disabled = false }) {
  const toggleDay = (key) => {
    if (disabled) return;
    onChange(
      'recurring_days',
      value.includes(key) ? value.filter((d) => d !== key) : [...value, key]
    );
  };

  return (
    <div className={`teacher-match-field ${disabled ? 'teacher-match-field-disabled' : ''}`}>
      <div className="teacher-match-field-label">Days of week</div>
      <div className="teacher-match-day-toggle" role="group" aria-label="Days of week">
        {recurringDays.map((day) => (
          <button
            key={day.key}
            type="button"
            className={`teacher-match-day ${value.includes(day.key) ? 'teacher-match-day-selected' : ''}`}
            onClick={() => toggleDay(day.key)}
            aria-pressed={value.includes(day.key)}
            disabled={disabled}
          >
            {day.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function RangeSlider({ filterKey, label, value, min, max, step, formatValue, unit, onChange }) {
  const [minValue, maxValue] = value;
  const percent = (current) => ((current - min) / (max - min)) * 100;

  const handleMinChange = (event) => {
    const next = Math.min(Number(event.target.value), maxValue);
    onChange(filterKey, [next, maxValue]);
  };

  const handleMaxChange = (event) => {
    const next = Math.max(Number(event.target.value), minValue);
    onChange(filterKey, [minValue, next]);
  };

  return (
    <div className="teacher-match-field">
      <div className="teacher-match-range-header">
        <div className="teacher-match-field-label">{label}</div>
        <div className="teacher-match-range-readout">
          {formatValue(minValue)} – {formatValue(maxValue)}
          {unit ? ` ${unit}` : ''}
        </div>
      </div>
      <div className="teacher-match-range">
        <div className="teacher-match-range-track">
          <div
            className="teacher-match-range-fill"
            style={{ left: `${percent(minValue)}%`, right: `${100 - percent(maxValue)}%` }}
          />
        </div>
        <input
          type="range"
          className="teacher-match-range-input"
          min={min}
          max={max}
          step={step}
          value={minValue}
          onChange={handleMinChange}
          aria-label={`${label} minimum`}
        />
        <input
          type="range"
          className="teacher-match-range-input"
          min={min}
          max={max}
          step={step}
          value={maxValue}
          onChange={handleMaxChange}
          aria-label={`${label} maximum`}
        />
      </div>
    </div>
  );
}

// Single-thumb ordinal slider for the minimum conversion level. The selected
// level and every better one are included (Any = no filter).
function ConversionRangeSlider({ value, onChange }) {
  const idx = typeof value === 'number' ? value : CONVERSION_MIN;
  const percent = (idx / CONVERSION_MAX) * 100;

  return (
    <div className="teacher-match-field">
      <div className="teacher-match-range-header">
        <div className="teacher-match-field-label">Conversion</div>
        <div className="teacher-match-range-readout">
          {idx === CONVERSION_MIN
            ? 'Any'
            : `${CONVERSION_LEVELS[idx]}${idx < CONVERSION_MAX ? ' & above' : ''}`}
        </div>
      </div>
      <div className="teacher-match-range">
        <div className="teacher-match-range-track">
          <div className="teacher-match-range-fill" style={{ left: `${percent}%`, right: '0%' }} />
        </div>
        <input
          type="range"
          className="teacher-match-range-input"
          min={CONVERSION_MIN}
          max={CONVERSION_MAX}
          step={1}
          value={idx}
          onChange={(event) => onChange('conversion', Number(event.target.value))}
          aria-label="Minimum conversion"
        />
      </div>
      <div className="teacher-match-range-scale">
        {CONVERSION_LEVELS.map((level) => (
          <span key={level}>{level}</span>
        ))}
      </div>
    </div>
  );
}

function AvailabilityTimeFilter({ windows, exactTimes, isOpen, onToggle, onChange }) {
  const [showExactTimes, setShowExactTimes] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const [draftWindows, setDraftWindows] = useState(windows);
  const [draftExactTimes, setDraftExactTimes] = useState(exactTimes);
  const ref = useRef(null);
  const selectedCount = windows.length + exactTimes.length;
  const triggerLabel = selectedCount === 0 ? 'All' : `${selectedCount} selected`;

  useEffect(() => {
    if (!isOpen) return;
    setDraftWindows(windows);
    setDraftExactTimes(exactTimes);
  }, [exactTimes, isOpen, windows]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const updatePopoverPosition = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const popoverWidth = 360;
      const popoverHeight = showExactTimes
        ? Math.min(620, window.innerHeight - 32)
        : Math.min(360, window.innerHeight - 32);
      const preferredLeft = rect.right + 18;
      const left =
        preferredLeft + popoverWidth <= window.innerWidth - 16
          ? preferredLeft
          : Math.max(16, window.innerWidth - popoverWidth - 16);
      const top = Math.max(
        16,
        Math.min(rect.top, Math.max(16, window.innerHeight - popoverHeight - 16))
      );

      setPopoverStyle({
        left,
        top
      });
    };

    updatePopoverPosition();
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onToggle('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
    };
  }, [isOpen, onToggle, showExactTimes]);

  const toggleTimePopover = () => {
    if (isOpen) {
      onToggle('');
      return;
    }

    const scrollContainer = ref.current?.closest('.teacher-match-filter-scroll');
    if (ref.current && scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const fieldRect = ref.current.getBoundingClientRect();
      const nextScrollTop = scrollContainer.scrollTop + fieldRect.top - containerRect.top - 14;
      scrollContainer.scrollTo({ top: Math.max(0, nextScrollTop) });
    } else {
      ref.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    window.requestAnimationFrame(() => onToggle('availability'));
  };

  const toggleDraftValue = (selectedValues, value, updateSelectedValues) => {
    updateSelectedValues(
      selectedValues.includes(value)
        ? selectedValues.filter((selectedValue) => selectedValue !== value)
        : [...selectedValues, value]
    );
  };

  const applyTimeFilters = () => {
    onChange('availability_windows', draftWindows);
    onChange('availability_exact_times', draftExactTimes);
    onToggle('');
  };

  return (
    <div className="teacher-match-field teacher-match-availability-field" ref={ref}>
      <div className="teacher-match-field-label">Session Time</div>
      <button
        type="button"
        className={`teacher-match-select-trigger ${selectedCount > 0 ? 'teacher-match-selected' : ''}`}
        onClick={toggleTimePopover}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span>{triggerLabel}</span>
        <ChevronDown size={15} className={isOpen ? 'teacher-match-chevron-open' : ''} />
      </button>

      {isOpen ? (
        <div
          className="teacher-match-availability-popover"
          role="dialog"
          aria-label="Session Time"
          style={popoverStyle}
        >
          <div className="teacher-match-availability-popover-title">
            Classes between these times
          </div>

          <div className="teacher-match-time-choice-list">
            {availabilityWindows.map((window) => (
              <label
                key={window.key}
                htmlFor={`teacher-match-availability-${window.key}`}
                className={`teacher-match-time-choice ${draftWindows.includes(window.key) ? 'teacher-match-time-choice-selected' : ''}`}
              >
                <input
                  id={`teacher-match-availability-${window.key}`}
                  type="checkbox"
                  checked={draftWindows.includes(window.key)}
                  onChange={() => toggleDraftValue(draftWindows, window.key, setDraftWindows)}
                />
                <span className="teacher-match-time-choice-box">
                  {draftWindows.includes(window.key) ? <Check size={14} /> : null}
                </span>
                <span>{window.label}</span>
              </label>
            ))}
          </div>

          <button
            type="button"
            className="teacher-match-exact-time-toggle"
            onClick={() => setShowExactTimes((current) => !current)}
          >
            <span>Or choose an exact time</span>
            <ChevronDown size={16} className={showExactTimes ? 'teacher-match-chevron-open' : ''} />
          </button>

          {showExactTimes ? (
            <div className="teacher-match-exact-time-list">
              {exactTimeOptions.map((time) => (
                <label
                  key={time.value}
                  htmlFor={`teacher-match-exact-time-${time.value.replace(':', '-')}`}
                  className={`teacher-match-time-choice ${draftExactTimes.includes(time.value) ? 'teacher-match-time-choice-selected' : ''}`}
                >
                  <input
                    id={`teacher-match-exact-time-${time.value.replace(':', '-')}`}
                    type="checkbox"
                    checked={draftExactTimes.includes(time.value)}
                    onChange={() =>
                      toggleDraftValue(draftExactTimes, time.value, setDraftExactTimes)
                    }
                  />
                  <span className="teacher-match-time-choice-box">
                    {draftExactTimes.includes(time.value) ? <Check size={14} /> : null}
                  </span>
                  <span>{time.label}</span>
                </label>
              ))}
            </div>
          ) : null}

          <button
            type="button"
            className="teacher-match-availability-apply"
            onClick={applyTimeFilters}
          >
            Apply Filter
          </button>
        </div>
      ) : null}
    </div>
  );
}

function TimezoneFilterDropdown({ value, isOpen, onToggle, onChange, countryNameByIso2 }) {
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const selectedLabel = getTimezoneLabel(value);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = timezoneOptions.filter((option) => {
    const countryName = (option.iso2 && countryNameByIso2[option.iso2]) || '';
    const aliases = (option.iso2 && COUNTRY_SEARCH_ALIASES[option.iso2]) || [];
    // Searchable by group, descriptive label, IANA value, country ISO code,
    // country name, and common informal aliases (e.g. "UK" for GB).
    const searchable =
      `${option.group} ${option.label} ${option.value} ${option.iso2 || ''} ${countryName} ${aliases.join(' ')}`.toLowerCase();
    return searchable.includes(normalizedQuery);
  });
  const hasSelection = value !== 'All';
  const dropUp = useDropDirection(ref, isOpen, 280);

  useEffect(() => {
    if (!isOpen) return undefined;
    setQuery('');
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onToggle('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  const selectTimezone = (nextValue) => {
    onChange('timezone', nextValue);
    onToggle('');
  };

  return (
    <div className={`teacher-match-field ${isOpen ? 'teacher-match-field-open' : ''}`} ref={ref}>
      <div className="teacher-match-field-label">Timezone</div>
      <button
        type="button"
        className={`teacher-match-select-trigger ${hasSelection ? 'teacher-match-selected' : ''}`}
        onClick={() => onToggle(isOpen ? '' : 'timezone')}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{selectedLabel}</span>
        <ChevronDown size={15} className={isOpen ? 'teacher-match-chevron-open' : ''} />
      </button>

      {isOpen ? (
        <div
          className={`teacher-match-options teacher-match-timezone-options ${dropUp ? 'teacher-match-options-up' : ''}`}
          role="listbox"
        >
          <div className="teacher-match-timezone-search">
            <Search size={14} />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by timezone, country or ISO code"
            />
          </div>
          <button
            type="button"
            className="teacher-match-option"
            onClick={() => selectTimezone('All')}
            role="option"
            aria-selected={value === 'All'}
          >
            <span>All</span>
            {value === 'All' ? <Check size={14} /> : null}
          </button>
          {filteredOptions.map((option) => (
            <button
              key={`${option.group}-${option.value}`}
              type="button"
              className="teacher-match-option teacher-match-timezone-option"
              onClick={() => selectTimezone(option.value)}
              role="option"
              aria-selected={value === option.value}
            >
              <span>
                <span className="teacher-match-timezone-option-label">{option.label}</span>
                <span className="teacher-match-timezone-option-value">{option.value}</span>
              </span>
              {value === option.value ? <Check size={14} /> : null}
            </button>
          ))}
          {filteredOptions.length === 0 ? (
            <div className="teacher-match-option-empty">No timezones found</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SessionDateFilter({ value = null, onChange, disabled = false }) {
  const ref = useRef(null);
  const popoverRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const [draft, setDraft] = useState(null);

  const today = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(() => addDays(today, SESSION_DATE_MAX_DAYS), [today]);

  // Selection lives in a draft until "Done" is clicked, so nothing is applied
  // while the user is still picking. Re-seed it from the applied value on open.
  useEffect(() => {
    if (open) setDraft(value || null);
  }, [open, value]);

  const draftStart = draft?.startDate ? new Date(draft.startDate) : today;
  const draftEnd = draft?.endDate ? new Date(draft.endDate) : draftStart;

  useEffect(() => {
    if (!open) return undefined;

    // Open to the right of the field, with the popover's bottom aligned to the
    // field's bottom so the Clear/Done actions stay on-screen. Height is measured
    // from the rendered popover so the alignment is exact.
    const updatePopoverPosition = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const popoverWidth = popoverRef.current?.offsetWidth || 288;
      const popoverHeight = popoverRef.current?.offsetHeight || 340;
      const preferredLeft = rect.right + 18;
      const left =
        preferredLeft + popoverWidth <= window.innerWidth - 16
          ? preferredLeft
          : Math.max(16, window.innerWidth - popoverWidth - 16);
      let top = rect.bottom - popoverHeight;
      top = Math.min(top, window.innerHeight - popoverHeight - 16);
      top = Math.max(16, top);
      setPopoverStyle({ left, top });
    };

    updatePopoverPosition();
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
    };
  }, [open]);

  const handleSelect = (ranges) => {
    const { selection } = ranges;
    setDraft({
      startDate: startOfDay(selection.startDate),
      endDate: startOfDay(selection.endDate)
    });
  };

  const clampToWindow = (date) => {
    const d = startOfDay(date);
    if (d < today) return today;
    if (d > maxDate) return maxDate;
    return d;
  };

  const applyPreset = (preset) => {
    if (!preset.range) {
      // "All" — no date filter.
      setDraft(null);
      return;
    }
    const [start, end] = preset.range(today);
    setDraft({ startDate: clampToWindow(start), endDate: clampToWindow(end) });
  };

  const applyDraft = () => {
    onChange('session_date', draft);
    setOpen(false);
  };

  // Cancel discards the in-progress draft and closes; the applied value is kept.
  const cancel = () => {
    setOpen(false);
  };

  return (
    <div
      className={`teacher-match-field teacher-match-date-field ${disabled ? 'teacher-match-field-disabled' : ''}`}
      ref={ref}
    >
      <div className="teacher-match-field-label">Session Date</div>
      <button
        type="button"
        className={`teacher-match-select-trigger ${value ? 'teacher-match-selected' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className="teacher-match-date-trigger">
          <CalendarDays size={14} />
          {value ? formatDateRangeLabel(value) : 'Any date'}
        </span>
        <ChevronDown size={15} className={open ? 'teacher-match-chevron-open' : ''} />
      </button>

      {open && !disabled ? (
        <div
          className="teacher-match-daterange-popover"
          role="dialog"
          aria-label="Session Date"
          style={popoverStyle}
          ref={popoverRef}
        >
          <div className="teacher-match-daterange-body">
            <div className="teacher-match-daterange-presets">
              {SESSION_DATE_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className="teacher-match-daterange-preset"
                  onClick={() => applyPreset(preset)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <DateRange
              ranges={[{ startDate: draftStart, endDate: draftEnd, key: 'selection' }]}
              onChange={handleSelect}
              minDate={today}
              maxDate={maxDate}
              months={2}
              direction="horizontal"
              moveRangeOnFirstSelection={false}
              showMonthAndYearPickers={false}
              showDateDisplay={false}
              showMonthArrow={false}
            />
          </div>
          <div className="teacher-match-daterange-actions">
            <button type="button" className="teacher-match-daterange-clear" onClick={cancel}>
              Cancel
            </button>
            <button type="button" className="teacher-match-daterange-done" onClick={applyDraft}>
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function GeoFilterControls({
  countryValue,
  stateValue,
  allowedCountries,
  selectedCountry = null,
  selectVersion,
  openFilter,
  onToggle,
  onCountryChange,
  onStateChange
}) {
  const countryFilter = {
    key: 'country',
    label: 'Teacher Country',
    options: allowedCountries.map((country) => country.name)
  };

  return (
    <>
      <FilterDropdown
        filter={countryFilter}
        value={countryValue}
        isOpen={openFilter === 'country'}
        onToggle={onToggle}
        onChange={(key, value) => onCountryChange(value)}
        searchable
      />

      <div className="teacher-match-field">
        <div className="teacher-match-field-label">State</div>
        <StateSelect
          key={`state-${selectedCountry?.id || 'none'}-${selectVersion}`}
          containerClassName="teacher-match-geo-select"
          inputClassName="teacher-match-geo-input"
          placeHolder={selectedCountry ? 'All states' : 'Select country first'}
          countryid={selectedCountry?.id || 0}
          defaultValue={stateValue !== 'All' ? stateValue : undefined}
          disabled={!selectedCountry}
          onChange={onStateChange}
        />
      </div>
    </>
  );
}

FilterDropdown.propTypes = {
  filter: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  searchable: PropTypes.bool
};

FilterDropdown.defaultProps = {
  searchable: false
};

FilterCheckbox.propTypes = {
  filter: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

FilterSegmentedControl.propTypes = {
  filter: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
      ])
    ).isRequired
  }).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string
};

FilterSegmentedControl.defaultProps = {
  className: ''
};

RecurringDaysSelector.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  disabled: PropTypes.bool
};

RangeSlider.propTypes = {
  filterKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.number).isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  formatValue: PropTypes.func.isRequired,
  unit: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

ConversionRangeSlider.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired
};

AvailabilityTimeFilter.propTypes = {
  windows: PropTypes.arrayOf(PropTypes.string).isRequired,
  exactTimes: PropTypes.arrayOf(PropTypes.string).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired
};

TimezoneFilterDropdown.propTypes = {
  value: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  countryNameByIso2: PropTypes.object
};

TimezoneFilterDropdown.defaultProps = {
  countryNameByIso2: {}
};

SessionDateFilter.propTypes = {
  // eslint-disable-next-line react/require-default-props
  value: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date)
  }),
  onChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  disabled: PropTypes.bool
};

GeoFilterControls.propTypes = {
  countryValue: PropTypes.string.isRequired,
  stateValue: PropTypes.string.isRequired,
  allowedCountries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      iso2: PropTypes.string
    })
  ).isRequired,
  // eslint-disable-next-line react/require-default-props
  selectedCountry: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    iso2: PropTypes.string
  }),
  selectVersion: PropTypes.number.isRequired,
  openFilter: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  onCountryChange: PropTypes.func.isRequired,
  onStateChange: PropTypes.func.isRequired
};

function TeacherMatch() {
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(getInitialFilters);
  const [openFilter, setOpenFilter] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [geoSelectVersion, setGeoSelectVersion] = useState(0);
  const [allowedCountries, setAllowedCountries] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  // UUIDs of teachers selected for bulk profile sharing.
  const [selectedUuids, setSelectedUuids] = useState(() => new Set());
  const resultsScrollRef = useRef(null);
  const dispatch = useDispatch();
  const activeFilterChips = getActiveFilterChips(filters, searchValue);

  useEffect(() => {
    resetDocumentScroll();
    const frame = window.requestAnimationFrame(resetDocumentScroll);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  // Load the country list once, limited to the countries present in ianaTimezones.
  useEffect(() => {
    let active = true;
    Promise.resolve(GetCountries()).then((all) => {
      if (!active) return;
      const allowed = all
        .filter((country) => TIMEZONE_COUNTRY_ISO2.has(country.iso2))
        .sort((a, b) => a.name.localeCompare(b.name));
      setAllowedCountries(allowed);
      // Resolve the default country object so the State dropdown is enabled.
      const defaultCountry = allowed.find((country) => country.name === DEFAULT_TEACHER_COUNTRY);
      if (defaultCountry) setSelectedCountry((prev) => prev || defaultCountry);
    });
    return () => {
      active = false;
    };
  }, []);

  // iso2 -> country name, so the timezone search can match by country name and ISO code.
  const countryNameByIso2 = useMemo(() => {
    const map = {};
    allowedCountries.forEach((country) => {
      if (country.iso2) map[country.iso2] = country.name;
    });
    return map;
  }, [allowedCountries]);

  useEffect(() => {
    resultsScrollRef.current?.scrollTo({ top: 0 });
  }, [filters, searchValue]);

  // Fetch matching teachers from the backend whenever the (server-side) filters
  // change. Debounced so dragging sliders doesn't fire a request per tick.
  useEffect(() => {
    let ignore = false;
    const timer = setTimeout(() => {
      setLoading(true);
      const url = getApiUrl('/v2/teachermatch/search', false, true);
      postApi(url, buildSearchPayload(filters), true)
        .then((resp) => {
          if (ignore) return;
          const data = Array.isArray(resp.data?.data) ? resp.data.data : [];
          setTeachers(data.map(mapCardToTeacher));
          setLoading(false);
        })
        .catch((e) => {
          if (ignore) return;
          setLoading(false);
          let errMessage;
          try {
            errMessage = e.response.data.message || e.response.data.reason;
          } catch (err) {
            errMessage = 'Failed to load teacher matches. Please contact the dev team.';
          }
          dispatch(actions.set_notification_variable(true, 'error', errMessage));
        });
    }, 350);

    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [filters, dispatch]);

  const selectedFilterCount = activeFilterChips.length;

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const setCountryFilter = (countryName) => {
    setGeoSelectVersion((version) => version + 1);
    if (!countryName || countryName === 'All') {
      setSelectedCountry(null);
      setFilters((prev) => ({ ...prev, country: 'All', state: 'All' }));
      return;
    }
    const country = allowedCountries.find((item) => item.name === countryName) || null;
    setSelectedCountry(country);
    setFilters((prev) => ({ ...prev, country: countryName, state: 'All' }));
  };

  const setStateFilter = (state) => setFilter('state', state.name);

  const removeFilterChip = (chip) => {
    if (chip.filterKey === 'search') {
      setSearchValue('');
      return;
    }

    if (chip.filterKey === 'country') {
      setSelectedCountry(null);
      setGeoSelectVersion((version) => version + 1);
      setFilters((prev) => ({ ...prev, country: 'All', state: 'All' }));
      return;
    }

    if (chip.filterKey === 'state') {
      setGeoSelectVersion((version) => version + 1);
      setFilter('state', 'All');
      return;
    }

    if (chip.filterKey === 'session_date') {
      setFilter('session_date', null);
      return;
    }

    if (chip.filterKey === 'experience') {
      setFilter('experience', [EXPERIENCE_MIN, EXPERIENCE_MAX]);
      return;
    }

    if (chip.filterKey === 'base_rate') {
      setFilter('base_rate', [BASE_RATE_MIN, BASE_RATE_MAX]);
      return;
    }

    if (chip.filterKey === 'platform_years') {
      setFilter('platform_years', [PLATFORM_YEARS_MIN, PLATFORM_YEARS_MAX]);
      return;
    }

    if (chip.filterKey === 'age') {
      setFilter('age', [AGE_MIN, AGE_MAX]);
      return;
    }

    if (chip.filterKey === 'conversion') {
      setFilter('conversion', CONVERSION_MIN);
      return;
    }

    setFilters((prev) => {
      const currentValue = prev[chip.filterKey];
      if (Array.isArray(currentValue)) {
        return {
          ...prev,
          [chip.filterKey]: currentValue.filter(
            (selectedValue) => selectedValue !== (chip.rawValue || chip.value)
          )
        };
      }

      return { ...prev, [chip.filterKey]: 'All' };
    });
  };

  const clearFilters = () => {
    setSearchValue('');
    // Reset clears everything — including the timezone and session date that
    // getInitialFilters seeds with defaults.
    setFilters({ ...getInitialFilters(), timezone: 'All', session_date: null });
    setOpenFilter('');
    // Country resets to its default (India), not blank.
    setSelectedCountry(
      allowedCountries.find((country) => country.name === DEFAULT_TEACHER_COUNTRY) || null
    );
    setGeoSelectVersion((version) => version + 1);
  };

  // Server-side filtering already applied the operational filters; here we only
  // narrow the fetched results by the free-text search box (name / location /
  // language / goal).
  const filteredTeachers = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return teachers;
    return teachers.filter(
      (teacher) =>
        teacher.name.toLowerCase().includes(query) ||
        teacher.location.toLowerCase().includes(query) ||
        teacher.language.toLowerCase().includes(query) ||
        teacher.goals.some((goal) => goal.toLowerCase().includes(query))
    );
  }, [teachers, searchValue]);

  const bestMatch = filteredTeachers[0];

  // Selected teachers, intersected with the currently loaded set so a stale
  // selection (from a prior search) never leaks into a bulk copy.
  const selectedTeachers = useMemo(
    () => teachers.filter((teacher) => selectedUuids.has(teacher.uuid)),
    [teachers, selectedUuids]
  );

  const toggleSelected = (uuid) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  };

  const clearSelection = () => setSelectedUuids(new Set());

  const notifyCopy = (ok, message) =>
    dispatch(
      actions.set_notification_variable(
        true,
        ok ? 'success' : 'error',
        ok ? message : 'Could not copy to clipboard. Please try again.'
      )
    );

  const handleCopyProfile = async (teacher) => {
    const ok = await copyToClipboard(buildShareMessage(teacher, filters));
    notifyCopy(ok, `Copied ${teacher.name}'s profile to clipboard.`);
  };

  const handleCopySelected = async () => {
    if (selectedTeachers.length === 0) return;
    const ok = await copyToClipboard(buildShareMessageForMany(selectedTeachers, filters));
    notifyCopy(
      ok,
      `Copied ${selectedTeachers.length} profile${selectedTeachers.length > 1 ? 's' : ''} to clipboard.`
    );
  };

  // Close the video popup on Escape.
  useEffect(() => {
    if (!activeVideo) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setActiveVideo(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activeVideo]);

  // react-tooltip render bridge: looks up the hovered teacher by uuid and shows
  // the score breakdown returned by the backend.
  const renderScoreTooltip = (uuid) => {
    const teacher = filteredTeachers.find((t) => t.uuid === uuid);
    if (!teacher || !teacher.match_factors || teacher.match_factors.length === 0) return null;
    return (
      <div className="teacher-match-score-tooltip">
        <div className="teacher-match-score-tooltip-title">Why {teacher.match_score}% match</div>
        {teacher.match_factors.map((factor) => (
          <div key={factor.label} className="teacher-match-score-tooltip-row">
            <span className="teacher-match-score-tooltip-label">{factor.label}</span>
            <span className="teacher-match-score-tooltip-value">{factor.value}</span>
          </div>
        ))}
        <div className="teacher-match-score-tooltip-note">% is relative to the top match</div>
      </div>
    );
  };

  return (
    <div className="teacher-match-page">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Teacher Match</title>
      </Helmet>

      <div className="teacher-match-grid">
        <aside className="teacher-match-filter-panel">
          <div className="teacher-match-panel-heading">
            <div>
              <div className="teacher-match-panel-title">Filters</div>
              <div className="teacher-match-muted">{selectedFilterCount} selected</div>
            </div>
          </div>

          <div className="teacher-match-filter-scroll">
            <div className="teacher-match-filter-group">
              <div className="teacher-match-filter-heading">Personality Dimensions</div>
              {dimensionFilters.map((filter) => (
                <FilterDropdown
                  key={filter.key}
                  filter={filter}
                  value={filters[filter.key]}
                  isOpen={openFilter === filter.key}
                  onToggle={setOpenFilter}
                  onChange={setFilter}
                />
              ))}
            </div>

            <div className="teacher-match-filter-group teacher-match-operational-group">
              <div className="teacher-match-filter-heading">Operational Filters</div>
              <GeoFilterControls
                countryValue={filters.country}
                stateValue={filters.state}
                allowedCountries={allowedCountries}
                selectedCountry={selectedCountry}
                selectVersion={geoSelectVersion}
                openFilter={openFilter}
                onToggle={setOpenFilter}
                onCountryChange={setCountryFilter}
                onStateChange={setStateFilter}
              />
              {operationalFilters.map((filter) => {
                if (filter.key === 'country' || filter.key === 'state') return null;
                // Rendered inside the Availability section via sessionFilters.
                if (sessionFilterKeys.includes(filter.key)) return null;

                if (filter.key === 'availability') {
                  return (
                    <div key={filter.key} className="teacher-match-availability-section">
                      <div className="teacher-match-availability-title">Availability</div>
                      <div className="teacher-match-availability-subfilters">
                        <TimezoneFilterDropdown
                          value={filters.timezone}
                          isOpen={openFilter === 'timezone'}
                          onToggle={setOpenFilter}
                          onChange={setFilter}
                          countryNameByIso2={countryNameByIso2}
                        />
                        {filters.timezone === 'All' ? (
                          <div className="teacher-match-field-hint">
                            Day &amp; time filters use {DEFAULT_AVAILABILITY_TZ}
                          </div>
                        ) : null}
                        <RecurringDaysSelector
                          value={filters.recurring_days || []}
                          onChange={setFilter}
                        />
                        <SessionDateFilter
                          value={filters.session_date || null}
                          onChange={setFilter}
                        />
                        <AvailabilityTimeFilter
                          windows={filters.availability_windows || []}
                          exactTimes={filters.availability_exact_times || []}
                          isOpen={openFilter === 'availability'}
                          onToggle={setOpenFilter}
                          onChange={setFilter}
                        />
                        {sessionFilters.map((sessionFilter) => (
                          <FilterCheckbox
                            key={sessionFilter.key}
                            filter={sessionFilter}
                            value={filters[sessionFilter.key]}
                            onChange={setFilter}
                          />
                        ))}
                        <FilterSegmentedControl
                          filter={sessionDurationFilter}
                          value={filters.session_duration || 'All'}
                          onChange={setFilter}
                        />
                      </div>
                    </div>
                  );
                }

                if (filter.key === 'experience') {
                  return (
                    <RangeSlider
                      key={filter.key}
                      filterKey="experience"
                      label={filter.label}
                      value={filters.experience}
                      min={EXPERIENCE_MIN}
                      max={EXPERIENCE_MAX}
                      step={1}
                      formatValue={formatExperienceValue}
                      unit="yrs"
                      onChange={setFilter}
                    />
                  );
                }

                if (filter.key === 'base_rate') {
                  return (
                    <RangeSlider
                      key={filter.key}
                      filterKey="base_rate"
                      label={filter.label}
                      value={filters.base_rate}
                      min={BASE_RATE_MIN}
                      max={BASE_RATE_MAX}
                      step={50}
                      formatValue={formatBaseRateValue}
                      unit=""
                      onChange={setFilter}
                    />
                  );
                }

                if (filter.key === 'platform_years') {
                  return (
                    <RangeSlider
                      key={filter.key}
                      filterKey="platform_years"
                      label={filter.label}
                      value={filters.platform_years}
                      min={PLATFORM_YEARS_MIN}
                      max={PLATFORM_YEARS_MAX}
                      step={1}
                      formatValue={formatPlatformYears}
                      unit=""
                      onChange={setFilter}
                    />
                  );
                }

                if (filter.key === 'age') {
                  return (
                    <RangeSlider
                      key={filter.key}
                      filterKey="age"
                      label={filter.label}
                      value={filters.age}
                      min={AGE_MIN}
                      max={AGE_MAX}
                      step={5}
                      formatValue={formatAgeValue}
                      unit=""
                      onChange={setFilter}
                    />
                  );
                }

                if (filter.key === 'conversion') {
                  return (
                    <ConversionRangeSlider
                      key={filter.key}
                      value={filters.conversion}
                      onChange={setFilter}
                    />
                  );
                }

                if (filter.key === 'gender') {
                  return (
                    <FilterSegmentedControl
                      key={filter.key}
                      filter={filter}
                      value={filters[filter.key]}
                      onChange={setFilter}
                    />
                  );
                }

                return filter.options.length === 1 ? (
                  <FilterCheckbox
                    key={filter.key}
                    filter={filter}
                    value={filters[filter.key]}
                    onChange={setFilter}
                  />
                ) : (
                  <FilterDropdown
                    key={filter.key}
                    filter={filter}
                    value={filters[filter.key]}
                    isOpen={openFilter === filter.key}
                    onToggle={setOpenFilter}
                    onChange={setFilter}
                    searchable={filter.key === 'language'}
                  />
                );
              })}
            </div>
          </div>
        </aside>

        <section className="teacher-match-results">
          <div className="teacher-match-results-top">
            <div className="teacher-match-results-title">Teacher Matches</div>
            <div className="teacher-match-toolbar">
              <div className="teacher-match-search">
                <Search size={16} />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search teacher, location, language, or goal"
                />
              </div>
              <button
                type="button"
                className="teacher-match-clear-btn"
                onClick={clearFilters}
                title="Reset filters"
              >
                <X size={14} />
                <span>Reset</span>
              </button>
            </div>

            {activeFilterChips.length > 0 ? (
              <div className="teacher-match-active-filters">
                {activeFilterChips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    className="teacher-match-filter-chip"
                    onClick={() => removeFilterChip(chip)}
                    title={`Remove ${chip.label}: ${chip.value}`}
                  >
                    <span className="teacher-match-filter-chip-label">{chip.label}</span>
                    <span className="teacher-match-filter-chip-value">{chip.value}</span>
                    <X size={13} />
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="teacher-match-results-scroll" ref={resultsScrollRef}>
            {bestMatch ? (
              <div className="teacher-match-recommendation">
                <div className="teacher-match-recommendation-icon">
                  <Sparkles size={18} />
                </div>
                <div>
                  <div className="teacher-match-eyebrow">Best match now</div>
                  <div className="teacher-match-recommendation-title">
                    {bestMatch.name}
                    {bestMatch.match_score != null ? ` - ${bestMatch.match_score}% match` : ''}
                  </div>
                  <div className="teacher-match-muted">
                    {profileTags(bestMatch.profile)
                      .map((tag) => `${tag.label}: ${tag.value}`)
                      .join(' - ')}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="teacher-match-results-header">
              <div>
                <div className="teacher-match-section-title">Teachers</div>
                <div className="teacher-match-muted">
                  Showing {filteredTeachers.length} of {teachers.length}
                </div>
              </div>
              {selectedTeachers.length > 0 ? (
                <div className="teacher-match-selection-actions">
                  <span className="teacher-match-muted">{selectedTeachers.length} selected</span>
                  <button
                    type="button"
                    className="teacher-match-clear-btn teacher-match-copy-selected-btn"
                    onClick={handleCopySelected}
                    title="Copy all selected profiles to clipboard"
                  >
                    <Copy size={14} />
                    <span>Copy selected</span>
                  </button>
                  <button
                    type="button"
                    className="teacher-match-clear-btn"
                    onClick={clearSelection}
                    title="Clear selection"
                  >
                    <X size={14} />
                    <span>Clear</span>
                  </button>
                </div>
              ) : null}
            </div>

            {loading ? <div className="teacher-match-empty">Loading teacher matches…</div> : null}

            {!loading && filteredTeachers.length === 0 ? (
              <div className="teacher-match-empty">No teachers match the selected filters.</div>
            ) : null}

            {!loading && filteredTeachers.length > 0 ? (
              <div className="teacher-match-list">
                {filteredTeachers.map((teacher) => (
                  <article
                    key={teacher.uuid}
                    className={`teacher-match-card ${
                      selectedUuids.has(teacher.uuid) ? 'teacher-match-card-selected' : ''
                    }`}
                  >
                    <div className="teacher-match-card-main">
                      {teacher.profile_photo ? (
                        <img
                          className="teacher-match-avatar teacher-match-avatar-img"
                          src={teacher.profile_photo}
                          alt={teacher.name}
                        />
                      ) : (
                        <div className="teacher-match-avatar">{getInitials(teacher.name)}</div>
                      )}
                      <div>
                        <div className="teacher-match-card-title-row">
                          <Link
                            to={`/teacherprofile?id=${encodeURIComponent(teacher.uuid)}`}
                            className="teacher-match-name"
                          >
                            {teacher.name}
                          </Link>
                          <span className="teacher-match-rank-score">
                            <span className="teacher-match-rank">#{teacher.rank}</span>
                            {teacher.match_score != null ? (
                              <span
                                className="teacher-match-score"
                                data-tooltip-id="teacher-match-score-tip"
                                data-tooltip-content={teacher.uuid}
                              >
                                {teacher.match_score}%
                              </span>
                            ) : null}
                          </span>
                        </div>
                        <div className="teacher-match-muted">{teacher.location}</div>
                        {teacher.language ? (
                          <div className="teacher-match-muted">{teacher.language}</div>
                        ) : null}
                        {teacher.video_url ? (
                          <button
                            type="button"
                            className="teacher-match-video-link"
                            onClick={() =>
                              setActiveVideo({ url: teacher.video_url, name: teacher.name })
                            }
                          >
                            <PlayCircle size={14} />
                            <span>Watch intro</span>
                          </button>
                        ) : null}
                        <div className="teacher-match-tags">
                          {profileTags(teacher.profile).map((tag) => (
                            <LabeledPill
                              key={tag.key}
                              label={tag.label}
                              value={tag.value}
                              tone="dimension"
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="teacher-match-meta">
                      <LabeledPill
                        label="Conversion"
                        value={
                          teacher.conversion_score != null
                            ? `${teacher.conversion} (score: ${teacher.conversion_score.toFixed(2)})`
                            : teacher.conversion
                        }
                        tone="meta"
                        icon={<CheckCircle2 size={15} />}
                      />
                      <LabeledPill
                        label="Experience"
                        value={teacher.experience ? `${teacher.experience} yrs` : '—'}
                        tone="meta"
                      />
                      <LabeledPill label="Base rate" value={teacher.base_rate} tone="meta" />
                    </div>

                    {teacher.goals.length > 0 ? (
                      <div className="teacher-match-goals">
                        <LabeledPill label="Goals" value={teacher.goals.join(', ')} tone="goal" />
                      </div>
                    ) : null}

                    {teacher.certificates.length > 0 ? (
                      <div className="teacher-match-goals">
                        <LabeledPill
                          label="Certificates"
                          value={teacher.certificates.join(', ')}
                          tone="meta"
                        />
                      </div>
                    ) : null}

                    <div className="teacher-match-card-actions">
                      <label className="teacher-match-select" htmlFor={`select-${teacher.uuid}`}>
                        <input
                          id={`select-${teacher.uuid}`}
                          type="checkbox"
                          checked={selectedUuids.has(teacher.uuid)}
                          onChange={() => toggleSelected(teacher.uuid)}
                        />
                        <span>Select</span>
                      </label>
                      <button
                        type="button"
                        className="teacher-match-clear-btn teacher-match-copy-btn"
                        onClick={() => handleCopyProfile(teacher)}
                        title="Copy this profile to clipboard"
                      >
                        <Copy size={14} />
                        <span>Copy profile</span>
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : null}
          </div>
        </section>
      </div>

      <ReactTooltip
        id="teacher-match-score-tip"
        place="left"
        className="teacher-match-score-tooltip-box"
        render={({ content }) => renderScoreTooltip(content)}
      />

      {activeVideo ? (
        <div className="teacher-match-video-overlay">
          <div className="teacher-match-video-modal">
            <div className="teacher-match-video-modal-head">
              <span className="teacher-match-video-modal-title">{activeVideo.name}</span>
              <button
                type="button"
                className="teacher-match-video-close"
                onClick={() => setActiveVideo(null)}
                aria-label="Close video"
              >
                <X size={18} />
              </button>
            </div>
            <VideoPlayer url={activeVideo.url} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default TeacherMatch;
